import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Wrench, Settings, Shield, Phone, ArrowRight, ArrowLeft,
  CheckCircle, AlertTriangle, Clock, MapPin, Loader2, Home, Building2, 
  Paintbrush, Layers, User, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TarifsPage = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedTarif, setSelectedTarif] = useState('');
  const [formData, setFormData] = useState({
    project_type: '',
    property_type: '',
    services: [],
    surface: '',
    rooms: '',
    budget: '',
    urgency: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
  });

  const tarifsElectricite = [
    {
      id: 'depannage',
      icon: AlertTriangle,
      title: 'DÉPANNAGE URGENCE',
      price: '89',
      unit: '€',
      color: 'from-red-500/20 to-red-500/5',
      borderColor: 'border-red-500/30',
      features: [
        'Dépannage électrique',
        'Recherche panne électrique',
        'Intervention rapide 24h/24',
      ],
    },
    {
      id: 'reparation',
      icon: Wrench,
      title: 'RÉPARATION & RÉNOVATION',
      price: '35',
      unit: '€',
      color: 'from-blue-500/20 to-blue-500/5',
      borderColor: 'border-blue-500/30',
      features: [
        'Réparation tableau électrique',
        'Réparation prise électrique',
        'Rénovation installation',
      ],
    },
    {
      id: 'installation',
      icon: Settings,
      title: 'INSTALLATION ÉLECTRIQUE',
      price: '45',
      unit: '€',
      color: 'from-gold/20 to-gold/5',
      borderColor: 'border-gold/30',
      popular: true,
      features: [
        'Installation électrique',
        'Installation tableau électrique',
        'Installation prise électrique',
      ],
    },
    {
      id: 'diagnostic',
      icon: Shield,
      title: 'DIAGNOSTIC & CONFORMITÉ',
      price: '75',
      unit: '€',
      color: 'from-green-500/20 to-green-500/5',
      borderColor: 'border-green-500/30',
      features: [
        'Contrôle et diagnostic',
        'Remise aux normes',
        'Mise en conformité NF C 15-100',
      ],
    },
  ];

  const tarifsPeinture = [
    {
      id: 'peinture-murs',
      icon: Paintbrush,
      title: 'PEINTURE MURS & PLAFONDS',
      price: '18',
      unit: '€/m²',
      color: 'from-blue-400/20 to-blue-400/5',
      borderColor: 'border-blue-400/30',
      popular: true,
      features: [
        'Peinture acrylique mate ou satinée',
        'Protection mobilier incluse',
        '2 couches finitions soignées',
      ],
    },
    {
      id: 'peinture-prep',
      icon: Layers,
      title: 'PRÉPARATION & ENDUIT',
      price: '22',
      unit: '€/m²',
      color: 'from-purple-400/20 to-purple-400/5',
      borderColor: 'border-purple-400/30',
      features: [
        'Rebouchage trous & fissures',
        'Enduit de lissage',
        'Ponçage avant peinture',
      ],
    },
    {
      id: 'peinture-boiseries',
      icon: Paintbrush,
      title: 'BOISERIES & MENUISERIES',
      price: '35',
      unit: '€/élément',
      color: 'from-amber-500/20 to-amber-500/5',
      borderColor: 'border-amber-500/30',
      features: [
        'Portes, fenêtres, plinthes',
        'Sous-couche + finition',
        'Peinture glycéro ou acrylique',
      ],
    },
    {
      id: 'peinture-facade',
      icon: Building2,
      title: 'PEINTURE FAÇADE',
      price: '32',
      unit: '€/m²',
      color: 'from-stone-500/20 to-stone-500/5',
      borderColor: 'border-stone-500/30',
      features: [
        'Nettoyage façade haute pression',
        'Traitement anti-mousse',
        'Peinture façade microporeuse',
      ],
    },
  ];

  const tarifsSols = [
    {
      id: 'sol-stratifie',
      icon: Layers,
      title: 'POSE STRATIFIÉ',
      price: '20',
      unit: '€/m²',
      color: 'from-teal-400/20 to-teal-400/5',
      borderColor: 'border-teal-400/30',
      features: [
        'Pose flottante clipsable',
        'Sous-couche acoustique incluse',
        'Plinthes en option',
      ],
    },
    {
      id: 'sol-parquet',
      icon: Layers,
      title: 'PARQUET FLOTTANT',
      price: '25',
      unit: '€/m²',
      color: 'from-amber-600/20 to-amber-600/5',
      borderColor: 'border-amber-600/30',
      popular: true,
      features: [
        'Parquet contrecollé chêne',
        'Pose en chevron ou droite',
        'Découpe & finitions soignées',
      ],
    },
    {
      id: 'sol-carrelage',
      icon: Layers,
      title: 'POSE CARRELAGE',
      price: '38',
      unit: '€/m²',
      color: 'from-slate-400/20 to-slate-400/5',
      borderColor: 'border-slate-400/30',
      features: [
        'Préparation du support',
        'Pose colle + joints',
        'Sol ou mur, intérieur/extérieur',
      ],
    },
    {
      id: 'sol-parquet-massif',
      icon: Layers,
      title: 'PARQUET MASSIF COLLÉ',
      price: '45',
      unit: '€/m²',
      color: 'from-orange-700/20 to-orange-700/5',
      borderColor: 'border-orange-700/30',
      features: [
        'Parquet massif chêne premium',
        'Pose collée pleine surface',
        'Finition huilée ou vitrifiée',
      ],
    },
  ];

  const categories = [
    { id: 'electricite', label: 'Électricité', icon: Zap, tarifs: tarifsElectricite, accent: 'text-yellow-400' },
    { id: 'peinture', label: 'Peinture', icon: Paintbrush, tarifs: tarifsPeinture, accent: 'text-blue-400' },
    { id: 'sols', label: 'Revêtements de sols', icon: Layers, tarifs: tarifsSols, accent: 'text-amber-500' },
  ];

  const [activeCategory, setActiveCategory] = useState('electricite');

  const projectTypes = [
    { id: 'renovation', icon: Wrench, label: 'Rénovation', desc: 'Travaux de rénovation' },
    { id: 'neuf', icon: Building2, label: 'Construction Neuve', desc: 'Installation neuve' },
    { id: 'depannage', icon: Zap, label: 'Dépannage', desc: 'Intervention urgente' },
  ];

  const propertyTypes = [
    { id: 'maison', icon: Home, label: 'Maison' },
    { id: 'appartement', icon: Building2, label: 'Appartement' },
    { id: 'local', icon: Building2, label: 'Local commercial' },
  ];

  const services = [
    { id: 'electricite', icon: Zap, label: 'Électricité', color: 'text-yellow-500' },
    { id: 'peinture', icon: Paintbrush, label: 'Peinture', color: 'text-blue-400' },
    { id: 'revetement', icon: Layers, label: 'Revêtements de sols', color: 'text-green-400' },
  ];

  const budgets = [
    { id: 'moins_5000', label: 'Moins de 5 000€' },
    { id: '5000_10000', label: '5 000€ - 10 000€' },
    { id: '10000_20000', label: '10 000€ - 20 000€' },
    { id: 'plus_20000', label: 'Plus de 20 000€' },
    { id: 'ne_sais_pas', label: 'Je ne sais pas encore' },
  ];

  const urgencies = [
    { id: 'urgent', label: 'Urgent (sous 48h)', color: 'text-red-500' },
    { id: 'semaine', label: 'Cette semaine', color: 'text-orange-400' },
    { id: 'mois', label: 'Ce mois-ci', color: 'text-yellow-400' },
    { id: 'flexible', label: 'Flexible', color: 'text-green-400' },
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleDevisClick = (tarifTitle) => {
    setSelectedTarif(tarifTitle);
    setShowWizard(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      try {
        emailjs.init(publicKey);
        console.log('EmailJS initialized with public key');
      } catch (e) {
        console.warn('EmailJS init failed', e);
      }
    } else {
      console.warn('REACT_APP_EMAILJS_PUBLIC_KEY not set');
    }
  }, []);



  const handleSubmit = async () => {
    setLoading(true);
    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_6wrtsqj';
      const templateId = process.env.REACT_APP_EMAILJS_TARIFS_TEMPLATE_ID || process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_57feevv';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        toast.error('Configuration EmailJS manquante. Utilisez mailto en attendant.');
        setLoading(false);
        return;
      }

      const subject = `Demande d'estimation${selectedTarif ? ` - ${selectedTarif}` : ''}`;

      // Convert ids to human-readable labels
      const projectTypeLabel = (projectTypes.find(p => p.id === formData.project_type) || {}).label || formData.project_type;
      const propertyTypeLabel = (propertyTypes.find(p => p.id === formData.property_type) || {}).label || formData.property_type;
      const servicesLabels = formData.services.map(sid => (services.find(s => s.id === sid) || {}).label || sid);

      const details = `Type de projet: ${projectTypeLabel}\nType de bien: ${propertyTypeLabel}\nServices: ${servicesLabels.join(', ')}\nSurface: ${formData.surface} m²\nPièces: ${formData.rooms}\nBudget: ${formData.budget}\nUrgence: ${formData.urgency}\n: ${formData.address}`;

      // Map fields to the EmailJS Tarifs HTML template variables
      const templateParams = {
        selectedTarif: selectedTarif || '',
        project_type: projectTypeLabel || '',
        property_type: propertyTypeLabel || '',
        services: servicesLabels.join(', '),
        surface: formData.surface || '',
        rooms: formData.rooms || '',
        budget: formData.budget || '',
        urgency: formData.urgency || '',
        from_name: formData.name || '',
        from_email: formData.email || '',
        phone: formData.phone || '',
        address: formData.address || '',
        message: formData.message || '',
        to_email: 'aldroubi.brico@gmail.com',
        // optional: keep subject for logging or fallback
        subject,
      };

      console.log('EmailJS send (Tarifs) - request:', { serviceId, templateId, templateParams });
      const res = await emailjs.send(serviceId, templateId, templateParams, publicKey);
      console.log('EmailJS send (Tarifs) - response:', res);

      setSuccess(true);
      toast.success('Demande envoyée ! Nous vous recontactons sous 24h.');
    } catch (err) {
      console.error('EmailJS send error:', err);
      // Fallback: try to copy address and open mailto with details
      try {
        const details = `Type de projet: ${(projectTypes.find(p => p.id === formData.project_type) || {}).label || formData.project_type}\nType de bien: ${(propertyTypes.find(p => p.id === formData.property_type) || {}).label || formData.property_type}\nServices: ${formData.services.map(sid => (services.find(s => s.id === sid) || {}).label || sid).join(', ')}\nSurface: ${formData.surface} m²\nPièces: ${formData.rooms}\nBudget: ${formData.budget}\nUrgence: ${formData.urgency}\nAdresse: ${formData.address}`;
        await navigator.clipboard.writeText('aldroubi.brico@gmail.com');
        toast.error("Envoi EmailJS échoué — adresse copiée. Ouverture du client mail...");
        const mailtoBody = `${details}\n\nMessage:\n${formData.message}`;
        const mailto = `mailto:aldroubi.brico@gmail.com?subject=${encodeURIComponent('Demande d\'estimation')}&body=${encodeURIComponent(mailtoBody)}`;
        window.location.href = mailto;
      } catch (copyErr) {
        console.warn('Clipboard write failed', copyErr);
        toast.error("Envoi EmailJS échoué — contactez aldroubi.brico@gmail.com");
      }
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.project_type;
      case 2: return formData.property_type;
      case 3: return formData.services.length > 0;
      case 4: return formData.surface;
      case 5: return formData.budget;
      case 6: return formData.urgency;
      case 7: return formData.name && formData.email && formData.phone;
      default: return true;
    }
  };

  // Success view
  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="luxury-card p-8 sm:p-12"
          >
            <div className="flex flex-col items-center justify-center" style={{zIndex:1}}>
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-500/20 rounded-full flex items-center justify-center mb-6 sm:mb-8" style={{zIndex:2}}>
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />
              </div>
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl text-white mb-4">Demande Envoyée !</h1>
            <p className="text-gray-400 mb-8 text-sm sm:text-base">
              Merci pour votre demande. Nous vous recontacterons 
              <span className="text-gold font-medium"> sous 24h maximum</span>.
            </p>
            <div className="bg-gold/10 border border-gold/20 rounded p-4 mb-8">
              <p className="text-gold text-sm">Besoin d'une réponse immédiate ?</p>
              <a href="tel:0618709889" className="text-white text-lg font-medium">
                Appelez le 06 18 70 98 89
              </a>
              <p className="text-gray-500 text-xs mt-1">Disponible 24h/24</p>
            </div>
            <button
              onClick={() => { 
                setSuccess(false); 
                setShowWizard(false);
                setStep(1);
                setFormData({
                  project_type: '', property_type: '', services: [], surface: '',
                  rooms: '', budget: '', urgency: '', name: '', email: '',
                  phone: '', address: '', message: '',
                });
              }}
              className="btn-gold-outline"
            >
              Retour aux tarifs
            </button>
          </motion.div>
        </div>
        <div className="mt-24"><Footer /></div>
      </div>
    );
  }

  // Wizard view
  if (showWizard) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="estimation-wizard">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="luxury-label text-gold">Estimation Gratuite</span>
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
              {selectedTarif ? `Devis ${selectedTarif}` : 'Estimez Votre Projet'}
            </h1>
            <p className="text-gray-400 mt-4">C'est simple, gratuit et sans engagement.</p>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-1 mb-8">
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${
                  step >= s ? 'w-8 bg-gold' : 'w-4 bg-white/10'
                }`}
              />
            ))}
          </div>

          {/* Form */}
          <div className="luxury-card p-6 sm:p-8 md:p-12">
            <AnimatePresence mode="wait">
              {/* Step 1: Project Type */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Votre projet concerne :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Sélectionnez le type de projet</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {projectTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData({ ...formData, project_type: type.id })}
                        className={`p-6 rounded border transition-all text-center hover:border-gold/50 ${
                          formData.project_type === type.id ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <type.icon className={`w-10 h-10 mx-auto mb-4 ${formData.project_type === type.id ? 'text-gold' : 'text-gray-400'}`} />
                        <h3 className="text-white font-medium">{type.label}</h3>
                        <p className="text-gray-500 text-sm mt-1">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Property Type */}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Type de bien :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Quel type de propriété ?</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData({ ...formData, property_type: type.id })}
                        className={`p-6 rounded border transition-all text-center hover:border-gold/50 ${
                          formData.property_type === type.id ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <type.icon className={`w-10 h-10 mx-auto mb-4 ${formData.property_type === type.id ? 'text-gold' : 'text-gray-400'}`} />
                        <h3 className="text-white font-medium">{type.label}</h3>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Services */}
              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Services souhaités :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Sélectionnez un ou plusieurs services</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceToggle(service.id)}
                        className={`p-6 rounded border transition-all text-center hover:border-gold/50 ${
                          formData.services.includes(service.id) ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <service.icon className={`w-10 h-10 mx-auto mb-4 ${formData.services.includes(service.id) ? 'text-gold' : service.color}`} />
                        <h3 className="text-white font-medium">{service.label}</h3>
                        {formData.services.includes(service.id) && (
                          <CheckCircle className="w-5 h-5 text-gold mx-auto mt-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 4: Surface & Rooms */}
              {step === 4 && (
                <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Dimensions du projet :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Surface et nombre de pièces</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="luxury-label">Surface approximative (m²)</label>
                      <input
                        type="number"
                        value={formData.surface}
                        onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                        className="luxury-input text-lg"
                        placeholder="Ex: 80"
                      />
                    </div>
                    <div>
                      <label className="luxury-label">Nombre de pièces</label>
                      <input
                        type="number"
                        value={formData.rooms}
                        onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                        className="luxury-input text-lg"
                        placeholder="Ex: 5"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 5: Budget */}
              {step === 5 && (
                <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Votre budget :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Estimation de votre enveloppe</p>
                  
                  <div className="space-y-3">
                    {budgets.map((budget) => (
                      <button
                        key={budget.id}
                        onClick={() => setFormData({ ...formData, budget: budget.label })}
                        className={`w-full p-4 rounded border transition-all text-left hover:border-gold/50 ${
                          formData.budget === budget.label ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <span className="text-white">{budget.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 6: Urgency */}
              {step === 6 && (
                <motion.div key="step6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Délai souhaité :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Quand souhaitez-vous démarrer ?</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {urgencies.map((urgency) => (
                      <button
                        key={urgency.id}
                        onClick={() => setFormData({ ...formData, urgency: urgency.label })}
                        className={`p-4 rounded border transition-all text-center hover:border-gold/50 ${
                          formData.urgency === urgency.label ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <span className={formData.urgency === urgency.label ? 'text-gold' : urgency.color}>
                          {urgency.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 7: Contact Info */}
              {step === 7 && (
                <motion.div key="step7" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-serif text-xl sm:text-2xl text-white mb-2">Vos coordonnées :</h2>
                  <p className="text-gray-400 mb-8 text-sm sm:text-base">Pour recevoir votre estimation</p>
                  
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="luxury-label">Nom complet *</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500 pointer-events-none" style={{zIndex:2}} />
                          {/* Correction anti-chevauchement icône/texte */}
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="luxury-input"
                            placeholder="Jean Dupont"
                            required
                            style={{paddingLeft:'3.5rem',position:'relative',zIndex:1,minHeight:'48px'}}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="luxury-label">Téléphone *</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500 pointer-events-none" style={{zIndex:2}} />
                          {/* Correction anti-chevauchement icône/texte */}
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="luxury-input"
                            placeholder="06 12 34 56 78"
                            required
                            style={{paddingLeft:'3.5rem',position:'relative',zIndex:1,minHeight:'48px'}}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="luxury-label">Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500 pointer-events-none" style={{zIndex:2}} />
                        {/* Correction anti-chevauchement icône/texte */}
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="luxury-input"
                          placeholder="jean@exemple.fr"
                          required
                          style={{paddingLeft:'3.5rem',position:'relative',zIndex:1,minHeight:'48px'}}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="luxury-label">Adresse du chantier</label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500 pointer-events-none" style={{zIndex:2}} />
                          {/* Correction anti-chevauchement icône/texte */}
                          <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            className="luxury-input"
                            placeholder="12 Rue de la Paix, 61200 Argentan"
                            style={{paddingLeft:'3.5rem',position:'relative',zIndex:1,minHeight:'48px'}}
                          />
                        </div>
                    </div>
                    
                    <div>
                      <label className="luxury-label">Message complémentaire</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        className="luxury-input resize-none"
                        placeholder="Décrivez votre projet..."
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              <button
                onClick={() => step > 1 ? setStep(step - 1) : setShowWizard(false)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {step > 1 ? 'Retour' : 'Annuler'}
              </button>
              
              {step < 7 ? (
                <button
                  onClick={() => canProceed() && setStep(step + 1)}
                  disabled={!canProceed()}
                  className="btn-gold flex items-center gap-2 disabled:opacity-50"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || !canProceed()}
                  className="btn-gold flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      Envoyer
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              ✓ Gratuit et sans engagement &nbsp;•&nbsp; ✓ Réponse sous 24h
            </p>
          </div>
        </div>
        <div className="mt-24"><Footer /></div>
      </div>
    );
  }

  // Main tarifs view
  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="tarifs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="luxury-label text-gold">Nos Tarifs</span>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
            Rapport <span className="text-gold">Qualité-Prix</span> Imbattable
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-sm sm:text-base">
            Électricité, Peinture et Revêtements de sols. Des prix transparents et compétitifs pour tous vos projets.
          </p>
        </div>

        {/* CTA Estimation */}
        <div className="mb-12">
          <motion.div 
            className="luxury-card p-6 sm:p-8 border-gold/30 border-2 text-center"
            whileHover={{ scale: 1.01 }}
          >
            <h2 className="font-serif text-xl sm:text-2xl text-white mb-3">
              Estimez Votre Projet <span className="text-gold">en 2 Minutes</span>
            </h2>
            <p className="text-gray-400 mb-6 text-sm sm:text-base">
              Remplissez notre formulaire pour recevoir une estimation personnalisée sous 24h.
            </p>
            <button 
              onClick={() => setShowWizard(true)}
              className="btn-gold inline-flex items-center gap-2"
            >
              Commencer l'Estimation
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8" data-testid="tarifs-categories">
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`tarifs-cat-${cat.id}`}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all border ${
                  isActive
                    ? 'bg-gold text-black border-gold shadow-[0_0_20px_rgba(212,175,55,0.4)]'
                    : 'bg-white/5 text-gray-300 border-white/10 hover:border-gold/40 hover:text-gold'
                }`}
              >
                <cat.icon className={`w-4 h-4 ${isActive ? 'text-black' : cat.accent}`} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Tarifs Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16">
          {categories.find(c => c.id === activeCategory).tarifs.map((tarif, index) => (
            <motion.div
              key={tarif.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              className={`relative luxury-card overflow-hidden ${tarif.borderColor} border-2`}
              data-testid={`tarif-${tarif.id}`}
            >
              {tarif.popular && (
                <div className="absolute top-0 right-0 bg-gold text-black text-xs font-bold px-3 py-1">
                  POPULAIRE
                </div>
              )}
              
              <div className={`bg-gradient-to-b ${tarif.color} p-4 sm:p-6`}>
                <tarif.icon className="w-10 h-10 sm:w-12 sm:h-12 text-gold mb-4" />
                <h3 className="font-serif text-sm sm:text-base text-white leading-tight">{tarif.title}</h3>
              </div>
              
              <div className="p-4 sm:p-6">
                <div className="mb-6">
                  <span className="text-gray-400 text-xs sm:text-sm">À partir de</span>
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-3xl sm:text-4xl text-gold">{tarif.price}</span>
                    <span className="text-gold text-base sm:text-lg">{tarif.unit}</span>
                  </div>
                </div>
                
                <ul className="space-y-2 sm:space-y-3 mb-6">
                  {tarif.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300 text-xs sm:text-sm">
                      <CheckCircle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleDevisClick(tarif.title)}
                  className={`w-full py-2.5 sm:py-3 rounded text-sm font-medium transition-all ${
                    tarif.popular
                      ? 'bg-gold text-black hover:bg-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Devis personnalisé
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Note tarifs indicatifs */}
        <div className="text-center mb-16 -mt-8">
          <p className="text-gray-500 text-xs sm:text-sm italic">
            * Tarifs indicatifs hors fournitures. Devis personnalisé gratuit selon votre projet.
          </p>
        </div>

        {/* Info Section */}
        <div className="luxury-card p-6 sm:p-8 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-serif text-xl sm:text-2xl text-white mb-4">
                <Zap className="w-6 h-6 text-gold inline mr-2" />
                Vous avez une <span className="text-gold">panne électrique ?</span>
              </h2>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                Une odeur suspecte ? Votre installation nécessite un dépannage rapide ?
                <span className="text-gold font-medium"> ALDROUBI Électricité</span>, votre électricien à Argentan, 
                se déplace chez vous dans les plus brefs délais.
              </p>
            </div>
            
            <div className="bg-gold/5 border border-gold/20 rounded-lg p-4 sm:p-6">
              <h3 className="text-gold font-medium mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Zone d'intervention
              </h3>
              <p className="text-gray-300 text-sm sm:text-base mb-4">
                Intervention sur <span className="text-white font-medium">Argentan, Caen</span> et 
                dans un rayon de <span className="text-white font-medium">60 km</span>.
              </p>
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Clock className="w-4 h-4" />
                Disponible 24h/24 - 7j/7
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-gray-400 mb-4 text-sm sm:text-base">Besoin d'une intervention immédiate ?</p>
          <a 
            href="tel:0618709889" 
            className="btn-gold inline-flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Appeler maintenant
          </a>
        </div>
      </div>

      <div className="mt-24"><Footer /></div>
    </div>
  );
};

export default TarifsPage;
